
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import { toast } from 'sonner';

interface ResetPasswordFormData {
  email: string;
}

const ResetPasswordDialog: React.FC = () => {
  const { resetPassword } = useAuth();
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const form = useForm<ResetPasswordFormData>();

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    setLoading(true);
    const { error } = await resetPassword(data.email);
    setLoading(false);

    if (error) {
      toast.error(`Error: ${error.message}`);
    } else {
      setResetSent(true);
      form.reset();
      toast.success("Password reset link sent. Please check your email.");
    }
  };

  return (
    <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
      <DialogTrigger asChild>
        <Button variant="link" size="sm" className="text-green-600 hover:text-green-700">
          Forgot your password?
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            Enter your email address and we'll send you a link to reset your password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(handleResetPassword)}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="reset-email" className="block text-sm font-medium">
                Email
              </label>
              <Input
                id="reset-email"
                type="email"
                placeholder="your@email.com"
                autoComplete="email"
                {...form.register('email', { required: true })}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">Cancel</Button>
            </DialogClose>
            <Button 
              type="submit" 
              disabled={loading || resetSent} 
              variant="green"
            >
              {loading ? 'Sending...' : resetSent ? 'Email Sent' : 'Send Reset Link'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordDialog;
